<template>
  <v-expansion-panel>
    <template v-slot:title>
      {{ t("org-table") }}
      <slot name="title"></slot>
    </template>
    <v-expansion-panel-text>
      <div v-if="loading" class="grid-loading">
        <v-progress-circular indeterminate />
      </div>
      <v-data-table
        v-else
        :headers="headers"
        :items="orgs"
        :search="search"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="t('search')"
            single-line
            variant="outlined"
            hide-details
            clearable
            autofocus
          ></v-text-field>
        </template>
        <template v-slot:body="{ items }">
          <tr
            v-for="item in items"
            :key="item.orgId"
            class="org"
            @click="emitClick(item)"
          >
            <td>{{ item.orgName }}</td>
            <!-- <td>{{ item.orgId }}</td> -->
            <td>{{ item.enterpriseClient }}</td>
            <td :class="matchClass(item.clientId)">{{ item.clientId }}</td>
            <td v-if="debugModeOn">{{ item.orgId }}</td>
            <td>
              <v-tooltip bottom v-if="item.locations?.length">
                <template v-slot:activator="{ props }">
                  <v-chip v-bind="props">
                    {{ item.locations?.length || 0 }}
                  </v-chip>
                </template>
                <div class="locations-info">
                  {{ item.locations?.join(", ") }}
                </div>
              </v-tooltip>
              <template v-if="search && getLocationValue(item.locations).length">
                {{ t("owns") }}: {{ getLocationValue(item.locations) }}
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { appSyncApolloClient } from "../../helpers/api";
import gql from "graphql-tag";
import { useI18n } from "vue-i18n";
import { useDebugStore } from "../../stores/debug";
const { t } = useI18n();

const emit = defineEmits(["clikOrg"]);

const { debugModeOn } = storeToRefs(useDebugStore());

const search = ref("");
const headers = computed(() => [
  { title: t("org-name"), value: "orgName" },
  // { title: "Org ID", value: "orgId" },
  { title: t("enterprise"), value: "enterpriseClient" },
  { title: t("client-id"), value: "clientId" },
  ...(debugModeOn.value ? [{ title: t("org-id"), value: "orgId" }] : []),
  { title: t("locations"), value: "locations" },
]);

const orgs = ref([]);
const loading = ref(false);

function getLocationValue(locations) {
  if (!locations?.length) return "";
  if (!search.value) return locations.length;
  return locations
    // .filter((loc) => loc.includes(search.value))
    .filter((loc) => `${loc}` === search.value)
    .join(", ");
}

async function getOrgs() {
  loading.value = true;
  const query = gql`
    query allOrgs($nextToken: String, $limit: Int!) {
      allOrgs(nextToken: $nextToken, limit: $limit) {
        orgs {
          clientId
          orgId
          orgName
          appAccess
          enterpriseClient
          locations
        }
        nextToken
      }
    }
  `;

  let resp;
  let nextToken;
  let allOrgs = [];
  // eslint-disable-next-line no-shadow
  const getOrgs = (nextToken, limit = 1000) =>
    appSyncApolloClient.query({
      fetchPolicy: "network-only",
      variables: {
        nextToken,
        limit,
      },
      query,
    });
  do {
    // eslint-disable-next-line no-await-in-loop
    resp = await getOrgs(nextToken);
    allOrgs = allOrgs.concat(resp.data.allOrgs.orgs);
    nextToken = resp.data.allOrgs && resp.data.allOrgs.nextToken;
  } while (nextToken);
  resp.data.allOrgs.orgs = allOrgs;
  // return resp.data.allOrgs.orgs;
  orgs.value = resp.data.allOrgs.orgs;
  loading.value = false;
}

onMounted(() => {
  getOrgs();
});

function emitClick(org) {
  emit("clikOrg", org);
}

function matchClass(val) {
  return {
    match: search.value === val,
  };
}
</script>
<i18n>
{
  "en": {
    "org-table": "Org Table",
    "search": "Search",
    "owns": "Owns",
    "org-name": "Org Name",
    "enterprise": "Enterprise",
    "client-id": "Client ID",
    "org-id": "Org ID",
    "locations": "Locations",
  },
  "es": {
    "org-table": "Tabla de Organizaciones",
    "search": "Buscar",
    "owns": "Es Dueño de",
    "org-name": "Nombre de Organización",
    "enterprise": "Empresa",
    "client-id": "ID del Cliente",
    "org-id": "Org ID",
    "locations": "Ubicaciones",
  }
}
</i18n>
<style scoped>
.locations-info {
  max-width: 200px;
}

.grid-loading {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.org:hover {
  color: black;
  background: #eee;
  cursor: pointer;
}

.search-container {
  margin: 10px;
  display: flex;
  gap: 10px;

  div {
    max-width: 200px;
  }
}

.search-btn {
  width: 100%;
  margin: 10px;
}

.match {
  background-color: rgba(255, 255, 0, .16);
}
</style>
